import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { apiClient, URL } from "api";

// =================================================================
// Query Keys
// =================================================================

export const anexoKeys = {
  all: ["anexos"] as const,
  lists: () => [...anexoKeys.all, "list"] as const,
  list: (prefixo: string) => [...anexoKeys.lists(), prefixo] as const,
};

// =================================================================
// Tipos
// =================================================================

export interface Attachment {
  Z07_FILIAL: string;
  Z07_CHAVE: string;
  Z07_DESC: string;
  Z07_PATH: string;
  R_E_C_N_O_: number;
  Z07_ORIGEM: string;
}

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

// =================================================================
// Funções da API
// =================================================================

/**
 * Lista anexos por prefixo
 */
async function fetchAnexos(
  prefixo: string
): Promise<ApiResponse<{ files: Attachment[] }>> {
  const response = await fetch("/api/prenotas/anexos", {
    method: "GET",
    headers: { prefixo },
  });

  if (!response.ok) {
    throw new Error(`Erro ao buscar anexos: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Upload de anexo
 */
async function uploadAnexo(payload: {
  file: File;
  seq: string;
  doc: string;
  description: string;
}): Promise<ApiResponse> {
  const { file, seq, doc, description } = payload;

  // 1. Salvar no Protheus (metadados)
  const metadataPayload = {
    REC: doc,
    ARQUIVOS: [
      {
        seq: 0, // Sempre 0 para pegar o último da sequência
        arq: file.name,
        desc: description,
      },
    ],
  };

  const metadataResponse = await apiClient.production.post(
    "/reidoapsdu/anexo",
    metadataPayload
  );

  // Pega o seq retornado pelo Protheus
  const protheusSeq = metadataResponse.data.seq;
  console.log("📋 Seq retornado pelo Protheus:", protheusSeq);

  // 2. Subir o arquivo físico no servidor com o seq do Protheus
  const formData = new FormData();
  formData.append("doc", doc);
  formData.append("seq", protheusSeq);
  formData.append("file", file);

  const fileResponse = await fetch("/api/apps/upload", {
    method: "POST",
    body: formData,
  });

  if (!fileResponse.ok) {
    throw new Error("Erro no upload do arquivo físico");
  }

  return metadataResponse.data;
}

/**
 * Download direto do servidor de arquivos
 */
async function downloadAnexoDireto(filename: string): Promise<Blob> {
  // Limpa o filename removendo espaços extras
  const cleanFilename = filename.trim();

  const response = await fetch(`${URL.in.API_APPS}/uploads/${cleanFilename}`, {
    method: "GET",
    headers: {
      Accept: "*/*",
    },
  });

  console.log("📡 Response status:", response.status, response.ok);

  if (!response.ok) {
    throw new Error(`Erro ao fazer download direto: ${response.status}`);
  }

  return response.blob();
}

/**
 * Deleta anexo
 */
async function deleteAnexo(filename: string): Promise<ApiResponse> {
  // Limpa o filename removendo espaços extras
  const cleanFilename = filename.trim();

  // 1. Deletar no Protheus (metadados)
  const protheusResponse = await apiClient.production.delete(
    `/reidoapsdu/arq/deletar?arq=${encodeURIComponent(cleanFilename)}`
  );

  // 2. Deletar arquivo físico no servidor
  const fileResponse = await fetch(
    `${URL.in.API_APPS}/delete/${encodeURIComponent(cleanFilename)}`,
    {
      method: "DELETE",
    }
  );

  if (!fileResponse.ok) {
    console.warn("⚠️ Erro ao deletar arquivo físico:", fileResponse.status);
  }

  return protheusResponse.data;
}

// =================================================================
// Hooks TanStack Query
// =================================================================

/**
 * Hook para listar anexos
 */
export function useAnexos(prefixo: string) {
  return useQuery({
    queryKey: anexoKeys.list(prefixo),
    queryFn: () => fetchAnexos(prefixo),
    enabled: !!prefixo && prefixo.trim().length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

/**
 * Hook para upload de anexo
 */
export function useUploadAnexo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: uploadAnexo,
    onSuccess: (data, variables) => {
      toast.success("Anexo enviado com sucesso!");

      // 3. Listar novamente (invalida queries para atualizar a lista)
      queryClient.invalidateQueries({ queryKey: anexoKeys.lists() });

      console.log("✅ Upload bem-sucedido:", data);
    },
    onError: (error) => {
      toast.error("Erro ao enviar anexo");
      console.error("❌ Erro no upload:", error);
    },
  });
}

/**
 * Hook para download direto
 */
export function useDownloadAnexoDireto() {
  return useMutation({
    mutationFn: downloadAnexoDireto,
    onSuccess: (blob, filename) => {
      try {
        // Abre o arquivo diretamente em nova aba
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
        window.URL.revokeObjectURL(url);

        toast.success("Arquivo aberto!");
        console.log("✅ Arquivo aberto com sucesso:", filename);
      } catch (error) {
        console.error("❌ Erro ao abrir arquivo:", error);
        toast.error("Erro ao abrir arquivo");
      }
    },
    onError: (error) => {
      console.error("❌ Erro no download direto:", error);
      toast.error("Erro ao fazer download direto");
    },
  });
}

/**
 * Hook para deletar anexo
 */
export function useDeleteAnexo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAnexo,
    onSuccess: (data, filename) => {
      toast.success("Anexo excluído com sucesso!");

      // Invalida queries relacionadas
      queryClient.invalidateQueries({ queryKey: anexoKeys.lists() });

      console.log("✅ Delete bem-sucedido:", data);
    },
    onError: (error) => {
      toast.error("Erro ao excluir anexo");
      console.error("❌ Erro no delete:", error);
    },
  });
}

// =================================================================
// Hook Combinado
// =================================================================

/**
 * Hook combinado para anexos com todas as operações
 */
export function useAnexosCompleto(prefixo: string) {
  const anexosQuery = useAnexos(prefixo);
  const uploadMutation = useUploadAnexo();
  const downloadDiretoMutation = useDownloadAnexoDireto();
  const deleteMutation = useDeleteAnexo();

  const handleUpload = (payload: {
    file: File;
    seq: string;
    doc: string;
    description: string;
  }) => {
    uploadMutation.mutate(payload);
  };

  const handleDownloadDireto = (filename: string) => {
    downloadDiretoMutation.mutate(filename);
  };

  const handleDelete = (filename: string) => {
    deleteMutation.mutate(filename);
  };

  return {
    // Query data
    anexos: anexosQuery.data?.data?.files || [],
    isLoading: anexosQuery.isLoading,
    refetch: anexosQuery.refetch,

    // Upload
    upload: handleUpload,
    isUploading: uploadMutation.isPending,

    // Download direto
    download: handleDownloadDireto,

    // Delete
    delete: handleDelete,
  };
}
